import moment from "moment-timezone";

/**
 * Half-open range overlap where a null end means "no end date".
 * Works for ISO instants and for YYYY-MM-DD local dates alike because both
 * compare correctly as strings within the same kind.
 */
export function rangesOverlap(aStart, aEnd, bStart, bEnd) {
  if (!aStart || !bStart) return false;
  const aBeforeB = aEnd !== null && aEnd !== undefined && aEnd !== "" && aEnd <= bStart;
  const bBeforeA = bEnd !== null && bEnd !== undefined && bEnd !== "" && bEnd <= aStart;
  return !aBeforeB && !bBeforeA;
}

const longDate = (value, timezoneName) =>
  timezoneName ? moment.tz(value, timezoneName).format("MMM D, YYYY") : moment.utc(value, "YYYY-MM-DD").format("MMM D, YYYY");

/**
 * Finds an active schedule for the same practitioner whose range overlaps the
 * proposed one and explains what to do. `endsAt` on schedules is an exclusive
 * ISO instant or null.
 */
export function scheduleConflictMessage({ schedules = [], scheduleId, startsAt, endsAt }) {
  const other = schedules.find(
    (schedule) =>
      schedule.status === "active" &&
      schedule.scheduleId !== scheduleId &&
      rangesOverlap(startsAt, endsAt, schedule.startsAt, schedule.endsAt)
  );
  if (!other) return null;
  const from = longDate(other.startsAt, other.timezoneName);
  if (!other.endsAt) {
    return `This practitioner already has a schedule with no end date starting ${from}. End or cancel that schedule before adding another.`;
  }
  const to = longDate(moment(other.endsAt).subtract(1, "millisecond"), other.timezoneName);
  return `This practitioner already has an active schedule from ${from} to ${to}. Choose dates that do not overlap, or edit that schedule instead.`;
}

/**
 * Finds an active work schedule (availability rule) on the same schedule whose
 * dates overlap the proposed one. Rule dates are inclusive local dates and
 * `effectiveTo` is null for an open-ended rule.
 */
export function ruleConflictMessage({ rules = [], availabilityRuleId, effectiveFrom, effectiveTo }) {
  const inclusiveEnd = (value) => (value ? moment.utc(value, "YYYY-MM-DD").add(1, "day").format("YYYY-MM-DD") : null);
  const other = rules.find(
    (rule) =>
      rule.active !== false &&
      rule.availabilityRuleId !== availabilityRuleId &&
      rangesOverlap(effectiveFrom, inclusiveEnd(effectiveTo), rule.effectiveFrom, inclusiveEnd(rule.effectiveTo))
  );
  if (!other) return null;
  const from = longDate(other.effectiveFrom);
  if (!other.effectiveTo) {
    return `This schedule already has a work schedule with no end date starting ${from}. Edit that work schedule, or give it an end date, before adding another.`;
  }
  return `This schedule already has a work schedule from ${from} to ${longDate(other.effectiveTo)}. Choose dates that do not overlap, or edit that work schedule instead.`;
}
